import { useState, useEffect } from 'react';

import _ from 'lodash';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { formulaFromLCLWM } from '../../utils/Formula.js';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';

import { settleRoute } from '../utils/routes.js';
import SidePaneRfqsSupplierSelection from './SidePane.rfqs.supplierselection.js';
import { pasteKeepingTables } from '../../utils/emailText.js';

export function RouteSelector(props) {
    const { routes, selectedRoute, setSelectedRoute } = props;

    return (
        <>
            <Typography variant='body1' sx={{ mb: 1, fontWeight: 800 }}>
                Route
            </Typography>
            <Typography variant='caption' component='div' sx={{ paddingBottom: '12px' }}>
                Please select the route from the list of routes for this opportunity.
            </Typography>
            {routes.length === 0 && (
                <Alert severity='error'>
                    No routes found for this opportunity.
                </Alert>
            )}
            {routes.length > 0 && (
                <Select
                    value={selectedRoute ? JSON.stringify(selectedRoute) : null}
                    onChange={(e) => {
                        setSelectedRoute(JSON.parse(e.target.value));
                    }}
                    fullWidth
                    MenuProps={{
                        sx: { zIndex: 10000001 },
                        PaperProps: {
                            sx: { zIndex: 10000001 }
                        }
                    }}
                >
                    {routes.map(r => (
                        <MenuItem value={JSON.stringify(r)} key={JSON.stringify(r)}>
                            {r.source?.text ? ` from ${r.source.text}` : ''}{r.destination?.text ? ` to ${r.destination.text}` : ''}
                        </MenuItem>
                    ))}
                </Select>
            )}
        </>
    );
}

// Build a formula string from the tiered/min formula object the AI prompt now
// emits for per-unit pricing with minimums or weight/volume tiers.
//
// Two shapes are handled:
//
//   1) W/M (weight-OR-measurement) — basis: 'w_m' OR tier rows that mix KG/TON
//      and CBM units. Output uses the LCL W/M formula the engine already knows:
//        max(perTon * tons, perCbm * cbm)
//      KG-priced tiers are converted to per-ton (×1000).
//
//   2) Plain slab tiers (single unit) — basis like 'chargeable_weight' or 'cbm',
//      output: max(MIN, x<=T1?x*P1 : x<=T2?x*P2 : x*Pn)
function buildAiTieredFormula(formula) {
    if (!formula || !Array.isArray(formula.tiers)) return null;
    const rawTiers = formula.tiers.filter(t => t && t.cost != null && !isNaN(parseFloat(t.cost)));
    if (rawTiers.length === 0) return null;

    // W/M detection: explicit basis, or tiers carry mixed cbm/kg-or-ton units.
    const unitOf = (t) => String(t.unit || formula.unit || '').toUpperCase();
    const hasCbm = rawTiers.some(t => unitOf(t) === 'CBM' || unitOf(t) === 'M3');
    const hasWeight = rawTiers.some(t => ['KG', 'TON', 'TONS', 'MT'].includes(unitOf(t)));
    const isWM = String(formula.basis || '').toLowerCase() === 'w_m' || (hasCbm && hasWeight);

    if (isWM) {
        const cbmTier = rawTiers.find(t => unitOf(t) === 'CBM' || unitOf(t) === 'M3');
        const weightTier = rawTiers.find(t => ['KG', 'TON', 'TONS', 'MT'].includes(unitOf(t)));
        // "EUR 35 per W/M" is a single rate charged on whichever of volume or weight comes out
        // higher, so it arrives as one tier carrying no unit of its own — neither a cbm row nor a
        // weight row. Reading only those two rows left both sides of the formula at zero and the
        // charge priced at nothing, which is worse than not parsing it at all: the rate looked
        // like it had been read and came out free. One rate stands for both sides.
        const single = rawTiers.length === 1 ? parseFloat(rawTiers[0].cost) : null;
        const fallback = single != null && !isNaN(single) ? single : 0;
        const perCbm = cbmTier ? parseFloat(cbmTier.cost) : fallback;
        const rawWeightCost = weightTier ? parseFloat(weightTier.cost) : fallback;
        // Engine binds `tons` to the cargo weight in tons; convert KG-priced tiers. Only a row
        // that says KG itself is converted — a W/M rate standing in for both sides is already
        // per ton, and multiplying it by a thousand would be a rate nobody quoted.
        const perTon = weightTier && unitOf(weightTier) === 'KG' ? rawWeightCost * 1000 : rawWeightCost;
        // A minimum on a W/M rate is a minimum on the whole charge, so it guards both sides. The
        // W/M branch used to drop it entirely, quietly losing "Minimum freight: EUR 70".
        const minCost = formula.minimum?.cost != null ? parseFloat(formula.minimum.cost) : null;
        const min = minCost != null && !isNaN(minCost) ? minCost : 0;
        return formulaFromLCLWM({ perCbm, perTon, minPerCbm: min, minPerTon: min });
    }

    const tiers = rawTiers
        .map(t => ({ from: parseFloat(t.from) || 0, to: t.to == null || t.to === '' ? null : parseFloat(t.to), cost: parseFloat(t.cost) }))
        .sort((a, b) => a.from - b.from);
    // Build right-to-left so the open-ended (largest) tier is the final fallback.
    let expr;
    for (let i = tiers.length - 1; i >= 0; i--) {
        const t = tiers[i];
        const leg = `x*${t.cost}`;
        if (i === tiers.length - 1) {
            expr = leg;
        } else {
            const cap = t.to != null ? t.to : tiers[i + 1].from;
            expr = `x<=${cap}?${leg}:${expr}`;
        }
    }
    const minCost = formula.minimum?.cost != null ? parseFloat(formula.minimum.cost) : null;
    if (minCost != null && !isNaN(minCost)) {
        expr = `max(${minCost},${expr})`;
    }
    return expr;
}

// Drop charges that won't render correctly downstream: missing/non-ISO currency
// (must be exactly 3 letters), and no parseable numeric cost or formula.
// The AI sometimes returns currency: "PERCENT" or empty cost, which break the rates UI.
function isValidAiCharge(charge) {
    if (!charge) return false;
    const currency = (charge.currency || '').trim() || (charge.formula?.minimum?.currency || '').trim();
    if (!/^[A-Za-z]{3}$/.test(currency)) return false;
    const costNum = parseFloat(charge.cost);
    const hasCost = !isNaN(costNum) && isFinite(costNum);
    const hasStringFormula = typeof charge.formula === 'string' && charge.formula.trim().length > 0;
    const hasObjectFormula = charge.formula && typeof charge.formula === 'object' && Array.isArray(charge.formula.tiers) && charge.formula.tiers.length > 0;
    return hasCost || hasStringFormula || hasObjectFormula;
}

export async function apiOfferToRates({ offer, request, unlocode, seaPorts }) {

    const rates = _.isArray(offer) ? offer : offer.rates;
    // Drop unusable charges before any further processing.
    rates.forEach(rate => {
        if (_.isArray(rate.charges)) {
            const before = rate.charges.length;
            rate.charges = rate.charges.filter(isValidAiCharge);
            const dropped = before - rate.charges.length;
            if (dropped > 0) console.log(`[apiOfferToRates] dropped ${dropped}/${before} invalid AI charge(s)`);
        }
    });
    const normalize = s => (s || '').replace(/[\u2018\u2019\u201C\u201D\u0060\u00B4]/g, "'").trim().toLowerCase();
    const normalizeType = t => { const n = normalize(t); return (n === 'by volume or weight (sea)' || n === 'by dimensions or weight (sea)') ? 'lcl' : n; };
    const filteredRates = rates.filter(rate => normalizeType(rate.product) === normalizeType(request.product.type) && !_.isEmpty(rate.charges)).filter(rate => {
        const charges = rate.charges.filter(c =>
            (c.cost !== null || (c.formula && (typeof c.formula === 'string' || (typeof c.formula === 'object' && Array.isArray(c.formula.tiers)))))
            && c.section !== null && c.field !== null
        );
        if (charges.length === 0) return false;
        return true;
    });

    const newRates = await Promise.all(filteredRates.map(async rate => {
        const sections = [];

        const [
            { result: source, resultType: sourceType },
            { result: destination, resultType: destinationType }
        ] = await Promise.all([
            settleRoute({ route: rate.source, country: rate.sourceCountry, unlocode, seaPorts, transportationMethod: rate.transportationMethod }),
            settleRoute({ route: rate.destination, country: rate.destinationCountry, unlocode, seaPorts, transportationMethod: rate.transportationMethod }),
        ]);

        const _source = source ? {
            text: `${source.code || source.iata_code ? `${source.code || source.iata_code} - ` : ''}${source.portname || source.name || source.text}`,
            countryCode: source.countryCode || source.iso_country || (source.code || source.portname).substr(0, 2),
            type: sourceType,
            id: source.iata_code || source.id || source.code,
        } : request.source;

        const _destination = destination ? {
            text: `${destination.code || destination.iata_code ? `${destination.code || destination.iata_code} - ` : ''}${destination.portname || destination.name || destination.text}`,
            countryCode: destination.countryCode || destination.iso_country || (destination.code || destination.portname).substr(0, 2),
            type: destinationType,
            id: destination.iata_code || destination.id || destination.code,
        } : request.destination;

        // Track each pushed field so we can build percent-of-total tax formulas in a
        // second pass once every charge's field id is known.
        const pushed = [];
        const isPercentFormula = (f) => !!f && typeof f === 'object' && (
            String(f.unit || '').toUpperCase() === 'PERCENT'
            || String(f.basis || '').toLowerCase() === 'total_charge'
            || String(f.basis || '').toLowerCase().includes('percent')
        );

        rate.charges.forEach(charge => {
            // The AI parser returns section: null when it can't classify a charge into
            // Origin/Freight/Destination (e.g. a catch-all like "all rest is 150$"). Fall back
            // to a real title so the charge is kept AND the offer passes schema validation
            // (offer.sections[].title must be a string — null is rejected).
            const sectionTitle = charge.section || 'Additional Charges';
            let section = sections.find(s => s.title === sectionTitle);
            if (!section) {
                section = {
                    id: uuidv4(),
                    title: sectionTitle,
                    offers: [{
                        id: uuidv4(),
                        fields: []
                    }]
                }
                sections.push(section);
            }
            const fallbackCurrency = rates.find(r => r.charges?.some(c => c.currency))?.charges.find(c => c.currency)?.currency || 'USD';
            const isPercent = isPercentFormula(charge.formula);
            // Resolve formula: prefer the object schema the new prompt emits; fall back
            // to a plain formula string the LLM may produce; otherwise just use cost.
            // Percent-of-total formulas are built in the second pass below.
            let formulaStr = null;
            if (!isPercent && charge.formula && typeof charge.formula === 'object') {
                formulaStr = buildAiTieredFormula(charge.formula);
            } else if (typeof charge.formula === 'string' && charge.formula.trim().length > 0) {
                formulaStr = charge.formula.trim().replace(/^=+/, '');
            }
            // Per-unit charges (and any charge with a formula that scales by x) must be
            // 'per-unit-type' so the engine multiplies by the product's chargeable unit.
            // Percent-of-total fields are 'flat' — their tax formula doesn't reference x.
            const isPerUnit = !isPercent && (charge.type !== 'flat' || (formulaStr && /\bx\b/.test(formulaStr)));
            const valueKey = isPerUnit ? request.product.id : 'flat';
            const currency = charge.currency || charge.formula?.minimum?.currency || fallbackCurrency;
            const fieldValue = formulaStr
                ? { value: 0, formula: formulaStr, currency }
                : { value: isPercent ? 0 : charge.cost, currency };
            const newField = {
                id: uuidv4(),
                title: charge.field,
                type: isPerUnit ? 'per-unit-type' : 'flat',
                values: { [valueKey]: fieldValue },
            };
            section.offers[0].fields.push(newField);
            pushed.push({ field: newField, charge, isPercent, currency });
        });

        // Second pass: build tax-style formulas for percent-of-total charges. The
        // engine recognizes max(min, <pct>/100*((field_<id>)+(field_<id>)+...)) via
        // formulaToParams; we reference every non-percent field in this rate.
        const taxTargets = pushed.filter(p => !p.isPercent).map(p => p.field.id);
        pushed.forEach(({ field, charge, isPercent, currency }) => {
            if (!isPercent) return;
            const tier = (charge.formula?.tiers || [])[0];
            const pct = parseFloat(tier?.cost ?? charge.formula?.cost);
            if (isNaN(pct) || taxTargets.length === 0) return;
            const refs = taxTargets.map(id => `(field_${id.replace(/-/g, '_')})`).join('+');
            field.type = 'flat';
            field.values = { flat: { value: 0, formula: `max(0,${pct}/100*(${refs}))`, currency } };
        });

        return {
            id: rate.id,
            product: request.product,
            source: _source,
            destination: _destination,
            transportationMethod: request.transportationMethod,
            ...(request.attributes ? { attributes: {
                conversationId: request.attributes?.conversationId,
                internetMessageId: request.attributes?.internetMessageId,
            }} : {}),
            ...(rate.platform ? { platform: rate.platform } : {}),
            supplier: {
                organization: request.supplier.organization,
                ...(request.supplier.uniqueId ? { uniqueId: request.supplier.uniqueId } : {}),
                ...(request.supplier.email ? { email: request.supplier.email } : {}),
            },
            offer: {
                id: uuidv4(),
                ...(rate.validity.from ? { validFrom: rate.validity.from } : {}),
                ...(rate.validity.to ? { validUntil: rate.validity.to } : {}),
                transitTime: request.transportationMethod === 'air' ?  ((rate.transitTime?.days || 0) * 24) + (rate.transitTime?.hours || 0) + ((rate.transitTime?.minutes || 0) / 60) :
                    (rate.transitTime?.days || 0) + ((rate.transitTime?.hours || 0) / 24) + ((rate.transitTime?.minutes || 0) / (24 * 60)),
                sections
            }
        };
    }));

    return newRates;
}

const joinWithAnd = arr => arr.length <= 2 ? arr.join(' and ') : `${arr.slice(0, -1).join(', ')} and ${arr.at(-1)}`;

export default function ApiClipboardDialog(props) {
    const { onClose, accesstoken, org, me, routes, suppliers, transportationMethod, products, setBuyingRates, unlocode, seaPorts } = props;
    const [ emailBody, setEmailBody ] = useState('');
    const [ loading, setLoading ] = useState(false);
    const [ aiGetInformationBase, setAiGetInformationBase ] = useState(null);
    const [ selectedRoute, setSelectedRoute ] = useState(routes.length > 0 ? routes[0] : null);
    const [ selectedSuppliers, setSelectedSuppliers ] = useState([]);
    const [ error, setError ] = useState({ noRoute: false, noSupplier: false });
    const [ adminConsoleOpen, setAdminConsoleOpen ] = useState(false);

    useEffect(() => {
        if (me?.email === 'admin@monada.ai') {
            (async () => {
                const { data: base } = await axios.get(`/api/emailconversations/ai-get-information-base?organizationId=${org.id}&accesstoken=${accesstoken}`);
                setAiGetInformationBase(base?.rate);
            })();
        }
    }, [me]);

    async function onDoIt() {
        if (!selectedRoute) {
            setError({ noRoute: true });
            return;
        }
        if (selectedSuppliers.length === 0) {
            setError({ noSupplier: true });
            return;
        }

        setError({ noRoute: false, noSupplier: false });

        setLoading(true);
        try {
            let { data: response } = await axios.post(`/api/emailconversations/ai-get-rates?accesstoken=${accesstoken}&organizationId=${org.id}`, {
                body: emailBody,
                subject: `Supplier Rates from ${selectedRoute.source?.text || 'Everywhere'} to ${selectedRoute.destination?.text || 'Everywhere'} for product ${products.map(p => p.type).join(', ')}`,
                ...(aiGetInformationBase ? { base: aiGetInformationBase } : {}),
            });

            const rates = await Promise.all(products.map(product => apiOfferToRates({
                offer: response,
                request: {
                    product,
                    source: selectedRoute.source,
                    destination: selectedRoute.destination,
                    transportationMethod,
                    supplier: {
                        organization: selectedSuppliers[0].name,
                        ...(selectedSuppliers[0].uniqueId ? { uniqueId: selectedSuppliers[0].uniqueId } : {}),
                        ...(selectedSuppliers[0].contacts?.[0]?.email ? { email: selectedSuppliers[0].contacts?.[0]?.email } : {}),
                    }
                },
                unlocode,
                seaPorts
            })));

            const cleanRates = _.compact(rates.flat());

            cleanRates.forEach(r => {
                r.attributes = {
                    sourceContent: emailBody,
                };
                r.platform = 'monada-ai-magic';
                r.created = new Date().getTime();
            });

            setLoading(false);

            if (cleanRates.length === 0) {
                const wrongProducts = _.uniq((_.isArray(response) ? response : response.rates).flat().map(r => r.product));
                setError({ noRates: true, wrongProducts });
                return;
            }

            setBuyingRates(br => [ ...br, ...cleanRates ]);
            onClose();
        } catch (e) {
            console.log(e);
            const aiMessage = e.response?.data?.detail;
            if (aiMessage) {
                setError({ aiError: aiMessage });
            } else {
                setError({ aiError: e.message || 'Failed to parse rates. Please try again.' });
            }
        }
        setLoading(false);
    }

    return (
        <Dialog open={true} onClose={onClose} fullWidth maxWidth='lg' sx={{ zIndex: 1000001 }}>
            <DialogTitle>
                Rates from Free Text
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <RouteSelector routes={routes} selectedRoute={selectedRoute} setSelectedRoute={setSelectedRoute} />
                </Box>
                <Box sx={{ mb: 2 }}>
                    <SidePaneRfqsSupplierSelection single={true} suppliers={suppliers} selectedSuppliers={selectedSuppliers} setSelectedSuppliers={setSelectedSuppliers} transportationMethod={transportationMethod} selectedRoute={selectedRoute} filterByEmail={false} />
                </Box>
                <Typography variant='body1' sx={{ mb: 1, fontWeight: 800 }}>
                    Content
                </Typography>
                <Typography variant='caption' component='div'>
                    Enter the content of the email you've received below, or text from any source, and we will magically extract rates from it.
                </Typography>
                <TextField
                    fullWidth
                    label='Email'
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    onPaste={e => pasteKeepingTables(e, setEmailBody)}
                    margin='normal'
                    multiline
                    rows={8}
                />
                {me?.email === 'admin@monada.ai' && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button onClick={() => setAdminConsoleOpen(a => !a)} sx={{ textTransform: 'none' }}>
                            {adminConsoleOpen ? 'Hide Admin Console' : 'Show Admin Console'}
                        </Button>
                    </Box>
                )}
                {me?.email === 'admin@monada.ai' && adminConsoleOpen && (
                    <>
                        <TextField
                            fullWidth
                            label={aiGetInformationBase ? 'ChatGPT Base' : 'ChatGPT Base (loading...)'}
                            value={aiGetInformationBase}
                            onChange={(e) => setAiGetInformationBase(e.target.value)}
                            margin='normal'
                            multiline
                            rows={8}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </>
                )}
                {error.noRoute && (
                    <Alert severity='error' sx={{ mb: 2 }}>
                        Please select a route.
                    </Alert>
                )}
                {!error.noRoute && error.noSupplier && (
                    <Alert severity='error' sx={{ mb: 2 }}>
                        Please select a supplier to assign the rates to.
                    </Alert>
                )}
                {error.noRates && error.wrongProducts?.length === 0 && (
                    <Alert severity='error' sx={{ mb: 2 }}>
                       Our AI could not generate any rates from the content given.
                    </Alert>
                )}
                {error.wrongProducts?.length > 0 && (
                    <Alert severity='error' sx={{ mb: 2 }}>
                        Received rates for {joinWithAnd(error.wrongProducts)} while requesting rates for {joinWithAnd(products.map(p => p.type))}.
                    </Alert>
                )}
                {error.aiError && (
                    <Alert severity='warning' sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 0.5 }}>Could not parse rates from this content</Typography>
                        <Typography sx={{ fontSize: '11px', color: '#64748b', whiteSpace: 'pre-wrap', maxHeight: '120px', overflowY: 'auto' }}>{error.aiError}</Typography>
                    </Alert>
                )}
                <Box sx={{ padding: '0px 20%' }}>
                    <Button color='primary' variant='contained' onClick={onDoIt} fullWidth disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : 'Do It!'}
                    </Button>
                </Box>
                <Box sx={{ padding: '0px 40%', marginTop: '12px' }}>
                    <Button color='primary' variant='text' onClick={onClose} fullWidth sx={{ textTransform: 'none' }}>Maybe later</Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
